import { SetStateAction, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Box } from '@material-ui/core';
import { InterventionStageProps, MasterDataEnumType } from '../CommonDataParts/InterventionTypes';
import { AnimalType, StageType } from '../../../../../../graphql/types';
import { DairyV2Farms } from '../../common';
import StageButtonBox from '../../StageButtonBox_v2';
import { DairyV2Baseline, DairyV2ContinuousGrowingStageData, DairyV2DairyCowStageData } from '../../../../models/Baseline/DairyV2Baseline';
import { Origin } from '../types';
import DairyV2HousingAndManureDialog from './DairyV2HousingAndManureDialog';
import DairyV2OutputContinuousGrowingDialog from './DairyV2OutputContinuousGrowingDialog';
import DairyV2OutputCowDialog from './DairyV2OutputCowDialog';
import DairyV2FeedDialog from './DairyV2FeedDialog';
import DairyV2EmissionMitigationDialog from './DairyV2EmissionMitigationDialog';
import DairyV2InputContinuousGrowingDialog from './DairyV2InputContinuousGrowingDialog';
import DairyV2InputCowsDialog from './DairyV2InputCowsDialog';
import { BaselineFeed, BaselineFeedWithOrigin } from '../../../../models/Baseline/Baseline';
import { FreshGrass } from '../../../../models/Baseline/CattleBaseline';
import { useGetAllDatabasesCompoundFeeds } from '../../../../../../api';
import featureFlags from '../../../../utils/featureFlags';
import { AnimalSystemEnum } from '../../Baseline/types';
import { getFeedsLoadingPlaceholder } from '../../../../helpers/loadingHelper';

interface DairyV2InterventionStagesProps extends Omit<InterventionStageProps, 'manureManagementSystems'> {
  farms: DairyV2Farms[];
  farmId: string;
  farmName: string;
  productionProcessName: string;
  baseline: DairyV2Baseline;
  origins: Origin[];
  animalType: AnimalType.DairyV2;
  stageType?: StageType;
}

enum StageDialogType {
  INPUT = 'input',
  OUTPUT = 'output',
  FEED = 'feed',
  HOUSING = 'housing',
  EMISSIONS = 'emissions',
}

const DairyV2InterventionStages = ({
  stageIndex,
  baseline,
  singleIngredients,
  compoundFeeds,
  origins,
  farms,
  formType,
  animalType,
  farmId,
  farmName,
  productionProcessName,
}: DairyV2InterventionStagesProps) => {

  const [activeDialog, setActiveDialog] = useState<StageDialogType | null>(null);

  const showDialog = (dialog: StageDialogType | null) => {
    setActiveDialog(dialog);
  };

  const fc = useFormContext();
  const stagePrefix = `stages.${stageIndex}`;
  const stageType: StageType = baseline?.stages?.[stageIndex]
    ?.type as StageType;

  let dairyV2StageData: DairyV2ContinuousGrowingStageData | DairyV2DairyCowStageData;

  if (stageType === StageType.ContinuousGrowing)
    dairyV2StageData = baseline?.stages?.[stageIndex]?.stageData as DairyV2ContinuousGrowingStageData;
  else if (stageType === StageType.DairyCow)
    dairyV2StageData = baseline?.stages?.[stageIndex]?.stageData as DairyV2DairyCowStageData;

  const getTotalCompoundFeedAmount = () => {
    let sum = 0;
    dairyV2StageData.feed.compoundFeeds?.forEach((feed: BaselineFeed) => {
      sum += Number(feed.kgPerAnimal);
    });
    return sum;
  }

  const getTotalSingleIngredientAmount = () => {
    let sum = 0;
    dairyV2StageData.feed.singleIngredients?.forEach((feed: BaselineFeedWithOrigin) => {
      sum += Number(feed.kgPerAnimal);
    });
    return sum;
  }

  const getTotalFreshGrassAmount = () => {
    let sum = 0;
    dairyV2StageData.feed.freshGrass?.forEach((feed: FreshGrass) => {
      sum += Number(feed.amount);
    });
    return sum;
  }
  const { data: fpCompoundFeeds, isLoading } = useGetAllDatabasesCompoundFeeds(AnimalSystemEnum.DAIRY_SYSTEM, featureFlags.FlagCoreConnectAPI)

  const getFeeds = () => {
    if(featureFlags.FlagCoreConnectAPI){
      if (isLoading) return [getFeedsLoadingPlaceholder(baseline?.info.databaseFoundation)];
      return fpCompoundFeeds || [];
    }
    return compoundFeeds;
  }

  return (
    <>
      <input
        ref={fc.register()}
        type="hidden"
        name={`${stagePrefix}.id`}
        value={`${baseline?.stages?.[stageIndex]?.id}`}
      />
      <input
        ref={fc.register()}
        type="hidden"
        name={`${stagePrefix}.name`}
        value={`${baseline?.stages?.[stageIndex]?.name}`}
      />
      <input
        ref={fc.register()}
        type="hidden"
        name={`${stagePrefix}.type`}
        value={stageType}
      />
      <input
        ref={fc.register()}
        type="hidden"
        name={`${stagePrefix}.index`}
        value={stageIndex}
      />

      <input
        ref={fc.register()}
        type="hidden"
        name={`${stagePrefix}.stageData.feed.baselineCompoundFeedTotal`}
        value={getTotalCompoundFeedAmount()}
      />
      <input
        ref={fc.register()}
        type="hidden"
        name={`${stagePrefix}.stageData.feed.baselineSingleIngredientTotal`}
        value={getTotalSingleIngredientAmount()}
      />
      <input
        ref={fc.register()}
        type="hidden"
        name={`${stagePrefix}.stageData.feed.baselineFreshGrassTotal`}
        value={getTotalFreshGrassAmount()}
      />

      {stageType === StageType.DairyCow && activeDialog === 'input' && (
        <DairyV2InputCowsDialog
          formType={formType}
          stageType={stageType}
          stageIndex={stageIndex}
          baseline={baseline}
          productionProcessName={productionProcessName}
          handleCancel={() => setActiveDialog(null)}
        />
      )}
      {stageType === StageType.ContinuousGrowing && activeDialog === 'input' && (
        <DairyV2InputContinuousGrowingDialog
          formType={formType}
          stageType={stageType}
          stageIndex={stageIndex}
          baseline={baseline}
          productionProcessName={productionProcessName}
          handleCancel={() => setActiveDialog(null)}
        />
      )}
      {activeDialog === 'feed' && (
        <DairyV2FeedDialog
          compoundFeeds={getFeeds()}
          singleIngredients={singleIngredients}
          origins={origins}
          formType={formType}
          baseline={baseline}
          stageIndex={stageIndex}
          stageType={stageType}
          handleCancel={() => setActiveDialog(null)}
        />
      )}
      {activeDialog === 'housing' && (
        <DairyV2HousingAndManureDialog
          formVisible={activeDialog === 'housing'}
          formType={formType}
          handleCancel={() => setActiveDialog(null)}
          baseline={baseline}
          stageIndex={stageIndex}
          stageType={stageType}
        />
      )}
      {stageType === StageType.DairyCow && activeDialog === 'output' && (
        <DairyV2OutputCowDialog
          formType={formType}
          stageIndex={stageIndex}
          baseline={baseline}
          handleCancel={() => setActiveDialog(null)}
          itemIndex={stageIndex}
        />
      )}
      {stageType === StageType.ContinuousGrowing && activeDialog === 'output' && (
        <DairyV2OutputContinuousGrowingDialog
          formType={formType}
          stageIndex={stageIndex}
          baseline={baseline}
          handleCancel={() => setActiveDialog(null)}
          itemIndex={stageIndex}
        />
      )}
      {activeDialog === 'emissions' && (
        <DairyV2EmissionMitigationDialog
          stageType={stageType}
          formType={formType}
          stageIndex={stageIndex}
          baseline={baseline}
          handleCancel={() => setActiveDialog(null)}
          itemIndex={stageIndex}
        />
      )}

      <Box
        style={{ display: "flex", width: "100%" }}
        flexDirection="row"
        justifyContent="space-between"
      >
        <StageButtonBox
          titleCode="SUSTELL.PROCESS.DIALOG.STAGE.ANIMALS.TITLE"
          iconCode="general/check-heart"
          description="SUSTELL.STAGE.INPUT_DATA"
          error={
            fc.errors?.stages && fc.errors?.stages[stageIndex]?.stageData?.input
          }
          handleOpen={() => showDialog(StageDialogType.INPUT)}
        />
        <StageButtonBox
          titleCode="SUSTELL.PROCESS.DIALOG.STAGE.FEED.TITLE"
          iconCode="shapes/cube-02"
          description="SUSTELL.STAGE.INPUT_DATA"
          error={
            fc.errors?.stages && fc.errors?.stages[stageIndex]?.stageData?.feed
          }
          handleOpen={() => showDialog(StageDialogType.FEED)}
        />
        <StageButtonBox
          titleCode="SUSTELL.PROCESS.DIALOG.STAGE.MANURE.TITLE"
          iconCode="general/building-06"
          description="SUSTELL.STAGE.INPUT_DATA"
          error={
            fc.errors?.stages &&
            fc.errors?.stages[stageIndex]?.stageData?.housing
          }
          handleOpen={() => showDialog(StageDialogType.HOUSING)}
        />
        <StageButtonBox
          titleCode="SUSTELL.PROCESS.DIALOG.STAGE.OUTPUT.TITLE"
          iconCode="arrows/arrow-circle-broken-right"
          description="SUSTELL.STAGE.INPUT_DATA"
          error={
            fc.errors?.stages &&
            fc.errors?.stages[stageIndex]?.stageData?.output
          }
          handleOpen={() => showDialog(StageDialogType.OUTPUT)}
        />
        <StageButtonBox
          titleCode="SUSTELL.PROCESS.DIALOG.STAGE.EMISSIONS.TITLE"
          iconCode="maps-travel/globe-04"
          description="SUSTELL.STAGE.INPUT_DATA"
          handleOpen={() => showDialog(StageDialogType.EMISSIONS)}
          error={
            fc.errors?.stages &&
            fc.errors?.stages[stageIndex]?.stageData?.emissions
          }
        />
      </Box>
    </>
  );
};

export default DairyV2InterventionStages;
